<template>
  <div class="padding">
    <div v-if="!submited">
      <div
        class="text-center no-data"
        v-if="!slide.title && !slide.titleDesc"
      >
        {{ $t("blankSlide") }}
      </div>
      <h2
        class="mt-0 text-left slide-title-style"
        v-if="slide.title || slide.title !== ' '"
        v-html="dataValue"
      />
      <p
        class="custom-mb-3 text-left title-desc slide-title-style"
        v-if="slide.titleDesc"
      >
        {{ slide.titleDesc }}
      </p>
      <div
        class="audio-control-slide"
        v-if="slide.audioLink && slide.audioPlayOnAudience && slide.id"
      >
        <AudioControl />
      </div>

      <ProgressBarTimer v-if="slide.type === 'openEnded'" />

      <div class="slide-img-wrapper" v-if="slide.image">
        <Loading v-if="isLoading" />
        <img
          :src="slide.image.large"
          alt
          class="slide-img"
          ref="slide-imgg"
          @load="_imgLoaded"
          v-else
        >
      </div>
      <div class="text-area" :class="{error: error}">
        <AutoSaveTextArea
          rows="4"
          v-model="text"
          :maxlength="limitText"
          :placeholder="$t('writeYourIdea')"
          @input="onInput"
        />
        <div class="count-text">
          {{ limitText - text.length }}
        </div>
      </div>

      <div v-if="shouldShowImageUploadForOpenEnded">
        <div v-if="!imageFile" class="upload-image-button" @click="showImageUploader">
          <div class="upload-image--icon">
            <ui-spinner
              class="upload-image--loader"
              :color="spinnerColor"
              v-if="isLoadingFilestackPicker"
            />
            <aha-icon
              name="image-choice"
              width="28"
              height="28"
              viewBox="0 0 30 30"
              v-else
            />
          </div>
          {{ $t("uploadImage") }}…
        </div>
        <div v-else class="image-viewer">
          <div class="image-wrapper">
            <img :src="imageFile.url">
          </div>
          <div class="image-actions">
            <a class="image-action image-action--change" @click="showImageUploader">
              <aha-icon
                name="image"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              />
              {{ $t("changeImage") }}
            </a>
            <a class="image-action image-action--remove" @click="removeImage">
              <aha-icon
                name="delete"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              />
              {{ $t("remove") }}
            </a>
          </div>
        </div>
      </div>

      <div
        class="label label-idea-count"
        v-if="slide.type === 'ideas' && ideas.length"
      >
        <span>{{ $t('youHaveMade') }}</span>
        <span class="idea-count" @click="showIdeaSubmitted = !showIdeaSubmitted">
          {{ ideas.length }} {{ $t('submissions') }}
          <i class="fas fa-chevron-up" v-if="!showIdeaSubmitted" />
          <i class="fas fa-chevron-down" v-if="showIdeaSubmitted" />
        </span>
      </div>

      <div
        class="custom-mb-3 label label-idea-count"
        v-if="isLimitIdea"
      >
        <span>{{ $t('maxExceededCannotSubmit') }}</span>
      </div>

      <div
        class="label"
        v-if="slide.openEndedMultipleOption && !isFirstTimeSubmit &&
          !isLimitIdea"
      >
        {{ $t("submitMultipleTimes") }}
      </div>

      <div
        class="submited"
        v-if="slide.type === 'openEnded' && isFirstTimeSubmit && !submited"
      >
        {{ $t("open-endedInstructionSubmission") }}
      </div>

      <!-- Multiple submit ideas messages -->
      <div
        class="label"
        v-if="isIdeasSubmitOnce && !slide.openEndedSubmited && !isLimitIdea"
      >
        {{ $t("youCanSubmitOnce") }}
      </div>
      <div
        class="label"
        v-if="isIdeasSubmitOnce && slide.openEndedSubmited"
      >
        {{ $t("eachCanOnlySubmitOnce") }}
      </div>
      <!-- End of multiple submit ideas messages -->

      <!-- show ideas submitted -->
      <IdeasSubmitted v-if="showIdeaSubmitted" />

      <!-- Additional fields -->
      <div
        class="audience-form"
        v-if="slide.additionalFields && slide.additionalFields.length"
      >
        <div v-for="(item, index) in slide.additionalFields" :key="index">
          <div class="input-label">
            <span v-if="item.label">
              <span>{{ item.label }}</span>
              <span v-show="item.required" class="pl-1 text-danger">*</span>
            </span>
          </div>
          <div v-if="item.type == 'name'">
            <!-- Name -->
            <div
              class="item-input"
              :class="{error: invalidAudienceName}"
            >
              <input
                type="text"
                class="item-input-text"
                :maxlength="maxInputLength"
                :disabled="checkDisableAudienceName()"
                id="openended-name"
                v-model="audienceName"
              >
              <div class="item-input-limit" v-if="!checkDisableAudienceName()">
                {{ maxInputLength - audienceName.length }}
              </div>
            </div>
            <div class="text-danger" v-if="invalidAudienceName">
              {{ $t("mandatoryFieldError") }}
            </div>
          </div>

          <div v-if="item.type == 'team'">
            <!-- Team -->
            <div class="item-input" :class="{error: invalidAudienceTeam}">
              <input
                type="text"
                class="item-input-text"
                :maxlength="maxInputLength"
                :disabled="checkDisableAudienceTeam()"
                id="openended-team"
                v-model="audienceTeam"
              >
              <div class="item-input-limit" v-if="!checkDisableAudienceTeam()">
                {{ maxInputLength - audienceTeam.length }}
              </div>
            </div>
            <div class="text-danger" v-if="invalidAudienceTeam">
              {{ $t("mandatoryFieldError") }}
            </div>
          </div>

          <div v-if="item.type == 'email'">
            <!-- Email -->
            <div class="item-input" :class="{error: invalidAudienceEmail}">
              <input
                type="email"
                class="item-input-text"
                :maxlength="maxInputLength"
                :disabled="checkDisableAudienceEmail()"
                id="openended-email"
                v-model="audienceEmail"
              >
              <div class="item-input-limit" v-if="!checkDisableAudienceEmail()">
                {{ maxInputLength - audienceEmail.length }}
              </div>
            </div>
            <div class="text-danger" v-if="invalidAudienceEmail">
              <span v-if="invalidEmailText !== ''">{{ invalidEmailText }}</span>
              <span v-else>{{ $t("mandatoryFieldError") }}</span>
            </div>
          </div>

          <div v-if="item.type == 'avatar' && isShownEmojiAvatar">
            <!-- Emoji -->
            <div
              class="item-input audience-emoji"
              :class="{'disabled': state.isAudienceEmojiDisabled}"
            >
              <div class="pick-emoji" v-click-outside="clickOutSideEmoji">
                <div class="horizontal layout center" @click="openPickEmoji()">
                  <div class="emoji-picked" v-if="$store.state.audienceEmoji">
                    {{ $store.state.audienceEmoji }}
                  </div>
                  <div
                    v-if="!openEmoji"
                    class="text-action"
                    :class="{'white': isTextColourLight}"
                  >
                    {{ $t("changeButton") }}
                  </div>
                  <div
                    v-if="openEmoji"
                    class="text-action"
                    :class="{'white': isTextColourLight}"
                  >
                    {{ $t("closeAvatar") }}
                  </div>
                </div>
                <picker
                  :data="emojiIndex"
                  set="emojione"
                  v-show="openEmoji"
                  @select="addEmoji"
                  v-if="emojiIndex"
                  :native="true"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="submit-button-wrapper">
        <aha-antd-button
          variant="primary-alt"
          block
          size="lg"
          class="openended--brainstorm-submit-button"
          type="submit"
          :disabled="checkDisableButtonSubmit && !submiting"
          :submitting="submiting"
          isLoadingInline
          @click="submit"
        >
          <template v-if="slide.stopSubmission && slide.type === 'openEnded'">
            <i class="fa fa-lock" />
            <span class="ml-2">{{ $t("submissionClosedButton") }}</span>
          </template>
          <template v-else>
            <span>
              {{ $t("submit") }}
              <CountDownTimer class="ml-1" />
            </span>
          </template>
        </aha-antd-button>
      </div>
      <!-- End of Open ended and brainstorm submit button -->

      <aha-antd-button
        v-if="slide.type === 'ideas' && $store.state.presentation.audiencePacing"
        variant="secondary"
        block
        size="lg"
        class="custom-mb-3"
        @click="startVoting"
      >
        <span>{{ $t('startVoting') }}</span>
      </aha-antd-button>

      <aha-antd-button
        v-show="isShowNextSlideButton"
        variant="secondary"
        block
        size="lg"
        class="custom-mb-3"
        :submitting="checkNextSlide"
        isLoadingInline
        @click="nextQuestion"
      >
        <span>{{ $t("nextSlideButton") }}</span>
      </aha-antd-button>

      <div class="stop-submission" v-if="slide.type === 'openEnded' && slide.stopSubmission">
        {{ $t("stopSubmission") }}
      </div>

      <div v-if="errorText && slide.type !== 'ideas'" class="mb-3 text-center text-danger">
        {{ errorText }}
      </div>
    </div>
    <SlideWaiting v-if="submited" />
  </div>
</template>
<script>
import { Picker, EmojiIndex } from 'emoji-mart-vue-fast'
import * as Sentry from '@sentry/vue'
import { mapState, mapActions, mapGetters } from 'vuex'
import Config from '@/config'
import SlideWaiting from '@/components/SlideWaiting'
import AutoSaveTextArea from '@/components/common/AutoSaveTextArea.vue'
import Loading from '@/components/Loading'
import Util from '@/util'
import EvenBus from '@/eventBus'
import data from '@/assets/data/all.json'
import AudioControl from '@/components/AudioControl.vue'
import storage from '@/services/storage'
import CountDownTimer from '@/components/CountDownTimer.vue'
import ProgressBarTimer from '@/components/ProgressBarTimer'
import audienceForm from '@/services/audienceForm'
import IdeasSubmitted from '@/components/types/IdeasSubmitted.vue'
import Filestack, { cleanFileName } from '@/services/Filestack'
import UserIsTypingMixin from '@/mixins/UserIsTypingMixin'
import Constant from '@/constant'

// UI
import UiSpinner from '@/components/UI/Spinner'
import ClickableTitleMixin from '@/mixins/ClickableTitleMixin'

const { maxInputLength } = Constant

const ideaLimit = 200
const FILESTACK_SUPPORTED_LANG = ['ca', 'da', 'de', 'en', 'es', 'fr', 'he', 'it', 'ja', 'ko', 'nl', 'no', 'pl', 'pt', 'sv', 'ru', 'vi', 'zh', 'tr', 'ar']
export default {
  components: {
    UiSpinner,
    SlideWaiting,
    Loading,
    Picker,
    AudioControl,
    ProgressBarTimer,
    CountDownTimer,
    IdeasSubmitted,
    AutoSaveTextArea,
  },
  mixins: [ClickableTitleMixin, UserIsTypingMixin],
  data() {
    return {
      submiting: false,
      text: '',
      limitText: 500,
      submited: false,
      isFirstTimeSubmit: false,
      error: false,
      checkNextSlide: false,
      errorText: '',
      isLoading: false,
      openEmoji: false,
      emojiIndex: null,
      audienceName: '',
      audienceTeam: '',
      audienceEmail: '',
      invalidAudienceName: false,
      invalidAudienceTeam: false,
      invalidAudienceEmail: false,
      invalidEmailText: '',
      isLimitIdea: false,
      ideasSubmitted: [],
      showIdeaSubmitted: false,
      isLoadingFilestackPicker: false,
      imageFile: null,
      timeoutInput: null,
      maxInputLength,
    }
  },
  computed: {
    ...mapGetters('zoomApp', ['isZoomApp']),
    ...mapGetters({
      slide: 'activeActivity',
    }),
    ...mapGetters('themeModule', ['isTextColourLight']),
    ...mapGetters('presentationSettings', ['isShownEmojiAvatar']),
    ...mapState({
      audienceId: (state) => state.audienceId,
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
      ideas: (state) => state.slideOptions,
      isIdeasSubmitOnce() {
        return !this.slide.openEndedMultipleOption && this.slide.type === 'ideas'
      },
      isAhaSlidesEU() {
        return window.location.origin.includes('ahaslides.eu')
      },
      noTextSubmitted() {
        return !this.text || (this.text && !this.text.trim())
      },
      hasNoTextAndImageSubmitted() {
        return this.slide.imageSubmission && this.noTextSubmitted &&
        (!this.imageFile || (this.imageFile && !this.imageFile.url))
      },
      imgSubmissionOffNoTextSubmitted() {
        return !this.slide.imageSubmission && this.noTextSubmitted
      },

      isLastSlide() {
        return this.presentation
        && this.slide
        && this.presentation.lastSlide === this.slide.id
      },
      isShowNextSlideButton() {
        return this.isFirstTimeSubmit
        && !this.submited
        && !this.presentation.audiencePacing
        && !this.isLastSlide
      },
    }),
    spinnerColor() {
      if (this.isTextColourLight) {
        return 'white'
      }

      return 'black'
    },
    state() {
      return this.$store.state
    },
    checkDisableButtonSubmit() {
      if ((this.slide.type === 'openEnded' && this.slide.stopSubmission) || this.isLimitIdea || (this.slide.type === 'ideas' && !this.slide.openEndedMultipleOption && this.ideas.length)) return true
      return false
    },
    shouldShowImageUploadForOpenEnded() {
      return !this.isAhaSlidesEU &&
        this.slide.type === 'openEnded' &&
        this.slide.imageSubmission
    },

  },
  watch: {
    slide: function handleSlideChange() {
      setTimeout(() => {
        if (this.$refs['slide-imgg'] && !this.$refs['slide-imgg'].complete) {
          this.isLoading = true
        }
      }, 1)

      if (typeof (this.slide.additionalFields) === 'string') {
        this.slide.additionalFields =
          JSON.parse(this.slide.additionalFields)
        this.$store.dispatch('updateSlide', this.slide)
      }
    },
  },
  mounted() {
    this.emojiIndex = new EmojiIndex(data)
    this.audienceName = this.$store.state.audienceName
    if (!this.$store.state.audienceName && this.currentUser && this.currentUser.id) {
      this.audienceName = this.currentUser.firstName
    }

    if (
      this.slide.openEndedSubmited
      && !this.slide.openEndedMultipleOption && this.slide.type === 'openEnded'
    ) {
      this.submited = true
    }

    if (!this.slide.additionalFields) {
      this.slide.additionalFields = []
    }

    try {
      if (typeof (this.slide.additionalFields) === 'string') {
        this.slide.additionalFields =
          JSON.parse(this.slide.additionalFields)
      }

      const presentationId = this.$store.state.presentation.id
      this.audienceTeam = storage.getValueByPresentation(presentationId, 'audienceTeam', '')
      this.$store.state.audienceTeam = this.audienceTeam

      this.audienceEmail = storage.getValueByPresentation(presentationId, 'audienceEmail', '')
      this.$store.state.audienceEmail = this.audienceEmail

      if (!this.$store.state.audienceEmail && this.currentUser && this.currentUser.id) {
        this.audienceEmail = this.currentUser.email
        this.$store.state.audienceEmail = this.currentUser.audienceEmail
      }
    } catch (error) {
      Sentry.captureException(error)
      this.slide.additionalFields = []
    }

    setTimeout(() => {
      if (this.$store.state.audienceName) {
        const elName = document.getElementById('openended-name')
        if (elName) {
          elName.disabled = true
        }
      }
      if (this.$store.state.audienceTeam) {
        const elTeam = document.getElementById('openended-team')
        if (elTeam) {
          elTeam.disabled = true
        }
      }
      if (this.$store.state.audienceEmail) {
        const elEmail = document.getElementById('openended-email')
        if (elEmail) {
          elEmail.disabled = true
        }
      }
    }, 1000)

    if (this.slide.type === 'openEnded') {
      this.checkSubmited()
    }

    if (this.slide.type === 'ideas') {
      if (this.slide.ideasCount >= ideaLimit) {
        this.isLimitIdea = true
      }
      this.getSlideOptionsAction()
    }

    EvenBus.$on('create-idea', () => {
      if (this.slide.ideasCount >= ideaLimit) {
        this.isLimitIdea = true
      }
    })

    EvenBus.$on('remove-idea', (socketData) => {
      this._handleRemoveIdea(socketData)
      this.slide.ideasCount = socketData.slide.ideasCount
      if (this.slide.ideasCount >= ideaLimit) {
        this.isLimitIdea = true
      } else {
        this.isLimitIdea = false
      }
    })
  },
  destroyed() {
    EvenBus.$off('create-idea')
    EvenBus.$off('remove-idea')
  },
  methods: {
    ...mapActions(['getSlideOptionsAction']),
    _handleRemoveIdea(socketData) {
      const index = this.ideas.findIndex((item) => {
        return item.id === socketData.slideOption.id
      })
      if (index !== -1) {
        this.getSlideOptionsAction()
      }
    },
    startVoting() {
      this.$store.dispatch('updateSlide', {
        votingStep: 'voting',
      })
      Util.mixpanelTrack(
        'Audience > Ideas',
        'Audience > Click start voting',
        '',
      )
    },
    checkSubmited() {
      const slideId = this.slide.id
      const presentationId = this.$store.state.presentation.id
      const answeredSlideIds = storage.getValueByPresentation(presentationId, 'answeredSlideIds', [])
      if (answeredSlideIds.includes(slideId)) {
        if (this.$store.state.presentation.lastSlide === slideId &&
         !this.slide.openEndedMultipleOption) {
          this.slide.isEndSlide = true
        }
        this.$store.dispatch('updateSlide', this.slide)
      }
    },
    checkDisableAudienceName() {
      if (this.$store.state.audienceName) return true
      return false
    },
    checkDisableAudienceTeam() {
      if (this.$store.state.audienceTeam) return true
      return false
    },
    checkDisableAudienceEmail() {
      if (this.$store.state.audienceEmail) return true
      return false
    },
    async submit() {
      this.error = false
      this.errorText = ''
      this.invalidEmailText = ''
      if (this.submiting || this.isLimitIdea || this.hasNoTextAndImageSubmitted ||
        this.imgSubmissionOffNoTextSubmitted) {
        this.error = true
        return
      }

      const { presentation } = this.state
      storage.updateValueByPresentation(this.presentation.id, 'audienceId', this.audienceId)
      const payload = {
        title: this.text,
        slideId: this.slide.id,
        audience: this.audienceId,
        presentationId: presentation.id,
        accessCode: presentation.uniqueAccessCode,
      }

      if (this.slide.imageSubmission && this.imageFile && this.imageFile.key) {
        payload.image = this.imageFile.key
      }

      if (!Array.isArray(this.slide.additionalFields)) {
        this.slide.additionalFields = []
      }

      const { additionalFields } = this.slide
      let errorAdditionalField = false
      this.invalidAudienceName = false
      this.invalidAudienceTeam = false
      this.invalidAudienceEmail = false

      const audienceNameField = additionalFields.find((item) => item.type === 'name')
      const audienceTeamField = additionalFields.find((item) => item.type === 'team')
      const audienceEmailField = additionalFields.find((item) => item.type === 'email')
      const audienceAvatarField = additionalFields.find((item) => item.type === 'avatar')

      // validate audience name field
      if (audienceNameField && audienceNameField.required) {
        if (!this.audienceName || !this.audienceName.trim()) {
          errorAdditionalField = true
          this.invalidAudienceName = true
        }
      }

      // validate audience team field
      if (audienceTeamField && audienceTeamField.required) {
        if (!this.audienceTeam || !this.audienceTeam.trim()) {
          errorAdditionalField = true
          this.invalidAudienceTeam = true
        }
      }

      // validate audience email field
      if (audienceEmailField && audienceEmailField.required) {
        if (!this.audienceEmail || !this.audienceEmail.trim()) {
          errorAdditionalField = true
          this.invalidAudienceEmail = true
          return
        }
      }

      if (audienceEmailField) {
        if (!Util.validateEmail(this.audienceEmail) && this.audienceEmail.trim() !== '') {
          errorAdditionalField = true
          this.invalidAudienceEmail = true
          this.invalidEmailText = this.$t('invalidEmail')
          return
        }
      }

      // if one of the required fields are not fulfill
      // stop the submission process
      if (errorAdditionalField) return

      // get audience name value
      if (audienceNameField && this.audienceName && this.audienceName.trim()) {
        this.$store.state.audienceName = this.audienceName

        storage.setValueByPresentation(presentation.id, 'audienceName', this.audienceName)
        payload.audienceName = this.audienceName
      }

      // get audience team value
      if (audienceTeamField && this.audienceTeam && this.audienceTeam.trim()) {
        this.$store.state.audienceTeam = this.audienceTeam
        storage.setValueByPresentation(presentation.id, 'audienceTeam', this.audienceTeam)
        payload.audienceTeam = this.audienceTeam
      }

      // get audience email value
      if (audienceEmailField && this.audienceEmail && this.audienceEmail.trim()) {
        this.$store.state.audienceEmail = this.audienceEmail
        storage.setValueByPresentation(presentation.id, 'audienceEmail', this.audienceEmail)
        payload.audienceEmail = this.audienceEmail
      }

      // get audience avatar value
      if (audienceAvatarField) {
        storage.setValueByPresentation(presentation.id, 'audienceEmoji', this.state.audienceEmoji)
        payload.audienceEmoji = this.state.audienceEmoji
        this.state.isAudienceEmojiDisabled = true
      }

      this.submiting = true

      try {
        await this.$http.post(Config.api.createOpenEndedOption, payload)
        this.text = ''
        this.submiting = false
        this.isFirstTimeSubmit = true
        if (!this.slide.openEndedMultipleOption) {
          if (this.slide.type === 'openEnded') {
            this.submited = true
          }
          this.slide.openEndedSubmited = true
          this.slide.submited = true
          if (this.slide.type === 'openEnded' && this.$store.state.presentation.lastSlide === this.slide.id) {
            this.slide.isEndSlide = true
          }
          this.$store.dispatch('updateSlide', this.slide)
        }

        const presentationId = this.$store.state.presentation.id
        const answeredSlideIds = storage.getValueByPresentation(presentationId, 'answeredSlideIds', [])
        answeredSlideIds.push(this.slide.id)
        storage.setValueByPresentation(presentationId, 'answeredSlideIds', answeredSlideIds)

        if (this.slide.type === 'ideas') {
          Util.mixpanelTrack(
            'Audience > Ideas',
            'Audience > Idea Submitted',
            '',
          )
          const submitedIdeas = storage.getValueByPresentation(presentationId, 'submitedIdeas', {})
          let ideaSumitted = submitedIdeas[this.slide.id]
          if (!ideaSumitted) ideaSumitted = 0
          ideaSumitted++
          submitedIdeas[this.slide.id] = ideaSumitted
          storage.setValueByPresentation(presentationId, 'submitedIdeas', submitedIdeas)
          this.getSlideOptionsAction()
          this.isFirstTimeSubmit = false
        } else if (this.slide.type === 'openEnded') {
          Util.mixpanelTrack(
            'Open Ended Submit',
            'Audience submit',
            'Audience submit',
          )
          if (
            this.$store.state.presentation.audiencePacing
            && !this.slide.openEndedMultipleOption
          ) {
            EvenBus.$emit('skipSlide')
          } else {
            EvenBus.$emit('confirmNextSlide')
          }
          this.imageFile = null
        }
        audienceForm.updateAudienceData()
      } catch (error) {
        Sentry.captureException(error)
        this.submiting = false
        if (error.body && error.body.message) {
          this._handleMaxIdeasError(error.body.message)
          this.errorText = error.body.message
          Util.mixpanelTrack(
            'Open Ended Submit',
            'Audience submit fail',
            error.body.message,
          )
        } else {
          this.errorText = this.$t('openendedSubmissionFail')
          Util.mixpanelTrack(
            'Open Ended Submit',
            'Audience submit fail',
            JSON.stringify(error),
          )
        }
      } finally {
        this.setUserTypingEnd()
      }
    },

    _handleMaxIdeasError(errorMessage) {
      if (errorMessage === 'Hit maximum amount of ideas') {
        this.isLimitIdea = true
      }
    },

    async nextQuestion() {
      if (this.$store.state.presentation.audiencePacing) {
        EvenBus.$emit('skipSlide')
        return
      }
      this.checkNextSlide = true
      this.slide.openEndedSubmited = true
      this.slide.submited = true

      storage.updateValueByPresentation(this.presentation.id, 'audienceId', this.audienceId)
      this.$http
        .get(Config.api.getPresentation + this.$store.state.presentation.uniqueAccessCode, {
          params: {
            audience: this.audienceId,
            continue: true,
          },
        })
        .then((res) => {
          this.checkNextSlide = false
          res.body = Util.formatSlide(res.body)
          if (res.body.additionalFields && typeof (res.body.additionalFields) === 'string') {
            res.body.additionalFields = JSON.parse(res.body.additionalFields)
          }
          Util.mixpanelTrack(
            'Open Ended Submit',
            'Audience Submit',
            'Audience choose Next Slide',
          )
          if (res.body.id !== this.slide.id) {
            this.$store.dispatch('setSlide', res.body)
            this.isFirstTimeSubmit = false
            this.submited = false
          } else {
            if (this.$store.state.presentation.lastSlide === this.slide.id) {
              this.slide.isEndSlide = true
              this.$store.dispatch('updateSlide', this.slide)
            }
            this.submited = true
          }
        })
        .catch(Sentry.captureException)
    },

    _imgLoaded() {
      this.isLoading = false
    },

    openPickEmoji() {
      this.openEmoji = !this.openEmoji
    },

    clickOutSideEmoji() {
      if (this.openEmoji) {
        this.openEmoji = false
      }
    },

    addEmoji(emoji) {
      this.$store.state.audienceEmoji = emoji.native
      this.openEmoji = false
    },

    showImageUploader() {
      const self = this
      this.isLoadingFilestackPicker = true
      let language = this.$i18n.locale
      // Filestack only support: 'ca', 'da', 'de', 'en', 'es', 'fr', 'he', 'it', 'ja',
      // 'ko', 'nl', 'no', 'pl', 'pt', 'sv', 'ru', 'vi', 'zh', 'tr', 'ar'
      // See: node_modules/filestack-js/src/schema/picker.schema.ts for more detail
      // Our lang code for Portuguese is 'br', which is not supported. So need to convert it to 'pt'
      if (language === 'br') {
        language = 'pt'
      }
      // If current locale is not listed in the supported languages of Filestack, default to 'en'
      if (!FILESTACK_SUPPORTED_LANG.includes(language)) {
        language = 'en'
      }
      const pickerOptions = {
        accept: ['image/*'],
        maxFiles: 1,
        lang: language,
        maxSize: 15 * 1024 * 1024, // max file size is 15MB
        fromSources: ['local_file_system', 'webcam'],
        uploadConfig: {
          retry: 3,
          timeout: 30000,
        },
        storeTo: {
          container: Config.filestack.bucket,
          region: Config.filestack.region,
        },
        onOpen() {
          setTimeout(() => {
            self.isLoadingFilestackPicker = false
          }, 500)
        },
        // Using to change selected file name, because the file name can contain special character,
        // and can cause an error when signing Cloudfront URL
        // NOTE: This currently only works for local file uploads
        onFileSelected(file) {
          return { ...file, filename: cleanFileName(file.filename) }
        },
        onFileUploadFinished: (file) => {
          this.imageFile = file

          Util.mixpanelTrack(
            'Open Ended',
            'Audience submit image succeed',
          )
        },
        onFileUploadFailed: (file, error) => {
          this.errorText = this.$t('openendedSubmissionFail')
          Sentry.captureException(error)
          Util.mixpanelTrack(
            'Open Ended',
            'Audience submit image fail',
          )
        },
      }
      Filestack.picker(pickerOptions).open()
    },
    removeImage() {
      this.imageFile = null
    },
    onInput() {
      this.setUserTypingStart() // UserIsTypingMixin
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";
.audio-control-slide {
  margin-bottom: 16px;
}
.no-data {
  padding: 16px;
}
.padding {
  padding: 0 16px;
}

.button-submit {
  margin-bottom: 16px;
}

.text-area {
  position: relative;

  textarea {
    width: 100%;
    padding: 10px;
    border-radius: 8px;
    border: 2px solid #ddd;
    -webkit-appearance: none;
    outline: none;
    padding: 10px 20px 10px 10px;
    margin-bottom: 10px;

    &:focus {
      border-color: $primary-color;
    }
  }

  .count-text {
    position: absolute;
    color: #313131;
    bottom: 26px;
    right: 9px;
    font-size: 12px;
  }

  &.error {
    textarea {
      border: 1px solid red;
      &:focus {
        border-color: red;
      }
    }

    .count-text {
      color: red;
    }
  }
}

.label,
.submited {
  text-align: center;
  color: inherit;
  margin-bottom: 16px;
  opacity: 0.7;
}

.slide-img-wrapper {
  width: auto;
  margin: 0 auto;
  overflow: hidden;
  padding-bottom: 0.83em;
  display: flex;
  flex-direction: row;
  align-items: center;
  & ::v-deep {
    .spinner {
      width: 120px;
      margin: 0 auto;
    }
    .spinner > div {
      width: 20px;
      height: 20px;
      margin: 0 8px;
    }
  }
}

.audience-form {
  .item-input {
    position: relative;
    margin-bottom: 16px;
    &.audience-emoji.disabled {
      .pick-emoji {
        cursor: not-allowed;
        .horizontal.layout {
          pointer-events: none;
        }
        .text-action {
          color: #ddd;
        }
      }
    }
  }
  .text-danger {
    text-align: left;
    margin-bottom: 16px;
    font-size: 13px;
  }
  .pick-emoji {
    position: relative;
    cursor: pointer;
    width: fit-content;
    -webkit-tap-highlight-color: transparent;

    .emoji-mart {
      position: absolute;
      top: 90px;
      left: 0;
      z-index: 110;
    }
  }

  .emoji-picked {
    font-size: 80px;
    margin: 0 16px;
  }

  .text-action {
    color: $primary-color;
    &.white {
      color: #fff;
      text-decoration: underline;
    }
  }
}
.label-idea-count {
  margin-bottom: 4px;
  opacity: 1;
  span {
    opacity: 0.7;
  }
  .idea-count {
    color: $primary-color;
    font-weight: bold;
    opacity: 1;
    cursor: pointer;
  }

  &.text-error {
    margin-bottom: 16px;
  }
}

.text-error {
  color: $danger-color;
}

.submit-button-wrapper {
  margin-bottom: 16px;
}

.upload-image-button {
  cursor: pointer;
  display: flex;
  width: max-content;
  margin-bottom: 16px;
  flex-direction: row;
  align-items: center;
  transition: all 0.2s ease;
  &:hover {
    color: #348EF6;
  }
  & > svg {
    margin-top: -2px;
  }
}
.upload-image--icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  margin-right: 6px;
}
.upload-image--loader {
  width: 22px;
  height: 22px;
  margin-left: 4px;
}
.image-viewer {
  padding: 16px;
  margin-bottom: 16px;
  border: 1px dashed #ccc;
  border-radius: 6px;
}
.image-actions {
  text-align: center;
  display: flex;
  justify-content: center;
  .image-action {
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: color 0.2s ease;
    font-size: 14px;
    &:hover {
      color: #348EF6;
    }
    & > svg {
      margin-right: 4px;
    }
  }
  .image-action--change {
    margin-right: 20px;
  }
}
.image-wrapper {
  margin-bottom: 16px;
  & > img {
    display: block;
    height: 100%;
    max-width: 100%;
    max-height: 300px;
    margin-left: auto;
    margin-right: auto;
  }
}

@media screen and (min-width: 421px) {
  .image-actions .image-action {
    font-size: 16px;
  }
  .image-actions .image-action--change {
    margin-right: 40px;
  }
}
</style>
